import { gql } from '@apollo/client';
import { Permission, RoleRBAC as RoleRBACType } from '../types';

// Mutation per login
export const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      user {
        id
        username
        email
        roleId {
          _id
          name
          displayName
          permissions {
            _id
            name
            resource
            action
            description
            category
            level
          }
        }
        membershipStatus
        profile {
          firstName
          lastName
          fullName
          profilePicture
          foto
          phoneNumber
        }
        membershipDetails {
          memberSince
          expiryDate
          membershipNumber
          membershipType
        }
        lastLogin
        emailVerified
      }
      accessToken
      refreshToken
    }
  }
`;

// Mutation per registrazione
export const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      user {
        id
        username
        email
        roleId {
          _id
          name
          displayName
        }
        membershipStatus
        profile {
          firstName
          lastName
          fullName
          foto
        }
        emailVerified
      }
      accessToken
      refreshToken
    }
  }
`;

// Query per ottenere utente corrente
export const ME_QUERY = gql`
  query Me {
    me {
      id
      username
      email
      roleId {
        _id
        name
        displayName
        permissions {
          _id
          name
          resource
          action
          description
          category
          level
        }
      }
      membershipStatus
      profile {
        firstName
        lastName
        fullName
        profilePicture
        foto
        phoneNumber
        bio
        specializations
        address {
          street
          city
          state
          zipCode
        }
      }
      membershipDetails {
        memberSince
        expiryDate
        membershipNumber
        membershipType
      }
      lastLogin
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

// Mutation per refresh token
export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;

// Mutation per logout
export const LOGOUT_MUTATION = gql`
  mutation Logout($refreshToken: String!) {
    logout(refreshToken: $refreshToken)
  }
`;

// Mutation per aggiornare profilo
export const UPDATE_PROFILE_MUTATION = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      username
      email
      profile {
        firstName
        lastName
        fullName
        profilePicture
        foto
        phoneNumber
        bio
        specializations
        address {
          street
          city
          state
          zipCode
        }
      }
      updatedAt
    }
  }
`;

// Mutation per cambiare password
export const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;

// Mutation per reset password
export const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;

// Mutation per reset password con token
export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`;

// Query per verificare email
export const VERIFY_EMAIL_MUTATION = gql`
  mutation VerifyEmail($token: String!) {
    verifyEmail(token: $token) {
      id
      emailVerified
    }
  }
`;

// Input Types
export interface LoginInput {
  identifier: string; // email or username
  password: string;
  rememberMe?: boolean;
}

// RegisterInput per il backend GraphQL (solo campi accettati)
export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

// RegisterFormData per il form frontend (include validazione client-side)
export interface RegisterFormData extends RegisterInput {
  confirmPassword: string;
  acceptTerms: boolean;
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  bio?: string;
  specializations?: string[];
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordInput {
  token: string;
  password: string;
  confirmPassword: string;
}

// Response Types
export interface User {
  id: string;
  username: string;
  email: string;
  roleId: RoleRBACType; // Sistema RBAC
  permissions?: Permission[]; // Permessi utente (derivati da roleId)
  membershipStatus: 'active' | 'pending' | 'expired' | 'suspended' | 'ACTIVE' | 'PENDING' | 'EXPIRED' | 'SUSPENDED'; // Supporta sia lowercase che UPPERCASE
  profile: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    profilePicture?: string;
    foto?: string;
    phoneNumber?: string;
    bio?: string;
    specializations?: string[];
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  membershipDetails?: {
    memberSince?: string;
    expiryDate?: string;
    membershipNumber?: string;
    membershipType?: 'standard' | 'premium' | 'corporate';
  };
  lastLogin?: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}
